import type { VueFinalModal, UseModalOptions } from 'vue-final-modal';

interface BottomSheetConfig {
    title?: string;
    disableAnimation?: boolean;
    zIndex?: number | string;
    noScrollFantasy?: boolean;
    fullHeight?: boolean;
}

export interface BottomSheet<R = any, P extends Component = any> {
    name?: string;
    component: P;
    attrs?: UseModalOptions<P>['attrs'] & BottomSheetConfig;
    resolve: (args: {
        reason: 'event' | 'model' | 'close';
        args?: [R, ...any[]];
    }) => void;
    ref?: { open: () => void; close: () => void };
}

interface BottomSheetConstructor<P extends Component> {
    component: P;
    attrs?: UseModalOptions<P>['attrs'] & BottomSheetConfig;
}

export const bottomSheets = shallowReactive<BottomSheet[]>([]);

export function useBottomSheet<
    R = any,
    P extends Component = InstanceType<typeof VueFinalModal>['$props'],
>(_options: BottomSheetConstructor<P>, bottomSheetId?: string) {
    const bottomSheet: BottomSheet<R, P> = {
        name: (_options.component as any).__name,
        component: _options.component,
        attrs: _options.attrs,
        resolve: () => {},
    };
    let id = bottomSheetId || (_options.component as any)?.__name || '';
    if (id) {
        id = `MrP${id}`;
    }
    const {
        isOpened: isClosableOpen,
        open: openClosable,
        close: closeClosable,
    } = useClosable(id);
    const close = () => {
        bottomSheet.ref?.close();
        closeClosable();
        nextTick(() => {
            // the bottom sheet will be closed then unmounted, if unmount is immediate,
            // the browser has no time to render closing animation.
            window.setTimeout(() => {
                if (!bottomSheets.length) {
                    return;
                }
                const index = bottomSheets.indexOf(bottomSheet);
                if (index > -1) {
                    bottomSheets.splice(index, 1);
                }
            }, 200);
        });
    };
    watch(isClosableOpen, value => {
        if (!value) {
            close();
        }
    });
    const asyncOpen = async (
        attrs?: UseModalOptions<P>['attrs'] & BottomSheetConfig
    ) => {
        bottomSheets.push(bottomSheet);
        if (attrs) {
            bottomSheet.attrs = {
                ...bottomSheet.attrs,
                ...attrs,
            };
        }
        const promise = new Promise<{
            reason: 'event' | 'model' | 'close';
            args?: [R, ...any[]];
        }>(_resolve => {
            bottomSheet.resolve = _resolve;
        });
        nextTick(() => {
            // the bottom sheet will be mounted then opened, if open is immediate,
            // the browser has no time to render opening animation.
            window.setTimeout(() => bottomSheet.ref?.open(), 200);
        });
        openClosable();
        return await promise;
    };
    return {
        open: asyncOpen,
        close,
    };
}
